export interface SimulatedCallTurnForDisplay {
  role: 'caller' | 'agent';
  text: string;
}

export interface ProposalGoalForDisplay {
  idn: string;
  title: string;
  description: string;
  source?: 'user' | 'assistant_proposed' | 'pack_default';
  tone?: string;
  liveliness?: 0 | 1 | 2 | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseGoalSource(value: unknown): ProposalGoalForDisplay['source'] | undefined {
  if (value === 'user' || value === 'assistant_proposed' || value === 'pack_default') {
    return value;
  }
  return undefined;
}

function parseLiveliness(value: unknown): 0 | 1 | 2 | null | undefined {
  if (value === null || value === 0 || value === 1 || value === 2) return value;
  return undefined;
}

export function normalizeClarificationSuggestions(suggestions: unknown): string[] {
  if (!Array.isArray(suggestions)) return [];
  return suggestions
    .map((suggestion) => stringValue(suggestion))
    .filter((suggestion) => suggestion.length > 0)
    .slice(0, 4);
}

export function hasRenderableInlineClarificationProposal(
  proposals: readonly unknown[] | null | undefined,
): boolean {
  if (!Array.isArray(proposals)) return false;
  return proposals.some((proposal) => {
    const record = asRecord(proposal);
    return (
      record?.kind === 'clarification_question' &&
      normalizeClarificationSuggestions(record.suggestions).length > 0
    );
  });
}

export function hasClarificationProposal(proposals: readonly unknown[] | null | undefined): boolean {
  if (!Array.isArray(proposals)) return false;
  return proposals.some((proposal) => asRecord(proposal)?.kind === 'clarification_question');
}

export function detectTextOnlyChoicePrompt(text: string): boolean {
  const normalized = text.toLocaleLowerCase();
  const russianChoice =
    /[\u0432][\u044b][\u0431][\u0435][\u0440][\u0438\u0438][\u0442\u0435]?/iu.test(normalized) ||
    /[\u0432][\u0430][\u0440][\u0438][\u0430][\u043d][\u0442]/iu.test(normalized) ||
    /[\u043d][\u0430][\u0436][\u043c][\u0438][\u0442][\u0435]?/iu.test(normalized);
  const englishChoice = /\b(?:choose|select|pick one|options?)\b/i.test(normalized);
  return russianChoice || englishChoice;
}

export function shouldShowTextOnlyChoiceFallback(input: {
  role: string;
  text: string;
  proposals: readonly unknown[] | null | undefined;
}): boolean {
  return (
    input.role === 'assistant' &&
    !hasClarificationProposal(input.proposals) &&
    !hasRenderableInlineClarificationProposal(input.proposals) &&
    detectTextOnlyChoicePrompt(input.text)
  );
}

export function normalizeSimulatedCallExchange(exchange: unknown): SimulatedCallTurnForDisplay[] {
  if (!Array.isArray(exchange)) return [];
  return exchange
    .map((turn) => {
      const record = asRecord(turn);
      if (!record) return null;
      const role = record.role === 'caller' || record.role === 'agent' ? record.role : null;
      const text = stringValue(record.text);
      return role && text ? { role, text } : null;
    })
    .filter((turn): turn is SimulatedCallTurnForDisplay => turn !== null);
}

export function normalizeGoalsForDisplay(goals: unknown): ProposalGoalForDisplay[] {
  if (!Array.isArray(goals)) return [];

  return goals
    .map((goal, index) => {
      const record = asRecord(goal);
      if (!record) return null;

      const title = stringValue(record.title);
      const description = stringValue(record.description);
      if (!title && !description) return null;

      const idn = stringValue(record.idn) || `goal_${index + 1}`;
      const tone = stringValue(record.tone);
      return {
        idn,
        title: title || description,
        description,
        ...(parseGoalSource(record.source) ? { source: parseGoalSource(record.source) } : {}),
        ...(tone ? { tone } : {}),
        ...(parseLiveliness(record.liveliness) !== undefined
          ? { liveliness: parseLiveliness(record.liveliness) }
          : {}),
      };
    })
    .filter((goal): goal is ProposalGoalForDisplay => goal !== null);
}
